<template>
  <base-button
    :aria-pressed="isZoomed"
    class="w-full"
    data-test-id="vf-zoomer"
    @blur="resetZoom(false)"
    @click="$viewport.lg ? toggleZoom() : handleDoubleClick($event)"
    @focus="isFocused = true"
    @keydown.down.prevent="mouseY = Math.min(mouseY + 50, h)"
    @keydown.enter="toggleZoom"
    @keydown.esc="resetZoom"
    @keydown.left.prevent="mouseX = Math.max(0, mouseX - 50)"
    @keydown.right.prevent="mouseX = Math.min(mouseX + 50, w)"
    @keydown.space="toggleZoom"
    @keydown.up.prevent="mouseY = Math.max(0, mouseY - 50)"
    @mouseleave="$viewport.lg && resetZoom(false)"
    @touchstart="isPinching && $event.preventDefault()"
  >
    <span class="sr-only">{{ $t.zoomIn }} - </span>
    <base-picture
      ref="target"
      v-bind="{ src, alt, height, width }"
      class="w-full"
      :props-img="{
        ...propsImg,
        class: ['duration', propsImg?.class, { 'op-0': isZoomed }],
        style: { transitionDelay: isZoomed ? undefined : '200ms' },
      }"
      :style="[
        {
          '--bg': `url(${_srcZoom})`,
          'background-image': 'var(--bg)',
          'background-repeat': 'no-repeat',
          'cursor': isZoomed ? 'zoom-out' : 'zoom-in',
          'touch-action': isPinching || isZoomed ? 'none' : 'auto',
          'transition': `background-size ${isPinching ? 0 : 200}ms linear`,
        },
      ]"
    />
  </base-button>
</template>

<script lang="ts" setup>
import type { BasePicture as BasePictureType } from '#components'
import type { Responsive } from '#types/common'
import type { ImgHTMLAttributes } from '#types/components/base/picture'
import type { Position } from '@vueuse/core'
import { usePinch } from '@vueuse/gesture'
import { useElementStyle } from '@vueuse/motion'

const props = defineProps<{
  /**
   * Source of the main image
   */
  src: Responsive | string
  /**
   * Source of the zoomed image to be displayed on hover
   */
  srcZoom: Responsive | string
  /**
   * Specifies width for various screen sizes
   */
  width: Responsive | string | number
  /**
   * Specifies height for various screen sizes
   */
  height: Responsive | string | number
  /**
   * Alt text for main image
   */
  alt?: string
  /**
   * Additional props for internal img tag
   */
  propsImg?: ImgHTMLAttributes
}>()

const emit = defineEmits<{
  zoom: [zoomFactor: number]
  swipe: [direction: number]
}>()

const MAX_ZOOM = 4
const MIN_ZOOM = 1
const TOGGLE_ZOOM = 2

const target = ref<InstanceType<typeof BasePictureType>>()
const zoomScale = ref(MIN_ZOOM)
const isPinching = ref(false)
const isToggled = ref(false)
const drag = reactive({
  startTime: 0,
  startX: 0,
  startY: 0,
  x: 0,
  y: 0
})

const { y: offsetY, x: offsetX } = useElementBounding(target)
const { style } = useElementStyle(target)
const {
  elementX: mouseX,
  elementY: mouseY,
  elementWidth: w,
  elementHeight: h,
  sourceType
} = useMouseInElement(target, { handleOutside: false })

const isFocused = ref(false)
const isZoomed = computed(() => zoomScale.value > MIN_ZOOM)
const _srcZoom = computed(() =>
  isObject(props.srcZoom) ? (props.srcZoom.lg || props.srcZoom.md || props.srcZoom.sm) : props.srcZoom
)
const x = computed(
  () => (sourceType.value !== 'touch' ? mouseX.value : drag.x) / w.value
)
const y = computed(
  () => (sourceType.value !== 'touch' ? mouseY.value : drag.y) / h.value
)

// When the zoom increases so does the drag speed, this keeps your finger in sync with the picture
const getZoomAccelerator = (value: number) => value / (zoomScale.value / 1.6)
const getVelocity = useDebounceFn((distance) => {
  const endTime = new Date().getTime()
  const time = endTime - drag.startTime // Time in milliseconds

  return distance / time // Velocity in pixels per millisecond
}, 100)

const { reset: resetPinch } = usePinch(({ offset: [d], pinching, origin: [originX, originY] }) => {
  const distance = d + (isToggled.value ? w.value / 2 : 0)

  // When starting the zoom interaction, originate from the center of the pinch
  if (zoomScale.value === MIN_ZOOM) {
    drag.x = originX - offsetX.value
    drag.y = originY - offsetY.value
    drag.startX = drag.x
    drag.startY = drag.y
  }
  zoomScale.value = Math.max(MIN_ZOOM, Math.min(1 + (distance / (w.value / 2)), MAX_ZOOM))
  isPinching.value = pinching
}, {
  domTarget: computed(() => target.value?.picture),
  distanceBounds: () => ({
    min: isToggled.value ? -w.value / 2 : 0,
    max: isToggled.value ? w.value / 2 : w.value,
  })
})

useDraggable(() => target.value?.picture, {
  preventDefault: true,
  onStart: (_, event: PointerEvent) => {
    if (isZoomed.value && event.pointerType === 'touch')
      drag.startTime = new Date().getTime()
  },
  onEnd: (_, event: PointerEvent) => {
    if (isZoomed.value && event.pointerType === 'touch') {
      drag.startX = drag.x
      drag.startY = drag.y
    }
  },
  onMove: (position: Position, event: PointerEvent) => {
    if (isZoomed.value && event.pointerType === 'touch') {
      if (isPinching.value) return
      getVelocity(position.x).then((velocity) => {
        const velocityThreshold = 0.7 // Velocity in pixels per millisecond

        if (Math.abs(velocity) > velocityThreshold)
          emit('swipe', Math.sign(position.x)) // Negative position.x LEFT; Positive position.x RIGHT
      })

      drag.x = Math.max(0, Math.min(drag.startX - getZoomAccelerator(position.x), w.value))
      drag.y = Math.max(0, Math.min(drag.startY - getZoomAccelerator((position.y - offsetY.value)), h.value))
    }
  }
})

const resetZoom = (includeEmit: boolean = true) => {
  if (!isZoomed.value) return
  zoomScale.value = MIN_ZOOM
  isToggled.value = false
  isFocused.value = false
  resetPinch()
  if (includeEmit) emit('zoom', 0)
}

const toggleZoom = useThrottleFn(() => {
  if (isZoomed.value) return resetZoom()
  isToggled.value = true
  zoomScale.value = TOGGLE_ZOOM
  drag.startX = w.value / 2
  drag.startY = h.value / 2
  drag.x = drag.startX
  drag.y = drag.startY
  emit('zoom', 1)
}, 250)

let lastClick = 0
const doubleClickThreshold = 300
const handleDoubleClick = (e: PointerEvent) => {
  if (isFocused.value || ((e.timeStamp - lastClick) < doubleClickThreshold))
    toggleZoom()

  lastClick = e.timeStamp
}

// Set styles
watch([x, y, zoomScale], ([x, y, zoom]) => {
  style.backgroundPosition = `${x * 100}% ${y * 100}%`
  style.backgroundSize = `${zoom * 100}%`
})

// If we are very close to being at min zoom, the user may not be able to tell
// Reset the zoom to enable touch events again
// I was locked out of touch events so many times testing this is justified
watch(isPinching, (pinching) => {
  if (!pinching && zoomScale.value <= 1.2)
    resetZoom()
})

defineExpose({ resetZoom })
</script>
